// src/components/ErrorBoundary.tsx
import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Result, Layout } from 'antd';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  private handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    window.location.href = '/team';
  };

  private handleGoHome = (): void => {
    window.location.href = '/team';
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Layout style={{ minHeight: '100vh' }}>
          <Layout.Content style={{ 
            padding: '50px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Result
              status="error"
              title="Something went wrong"
              subTitle={this.state.error?.message || 'An unexpected error occurred'}
              extra={[
                <Button
                  key="retry"
                  type="primary"
                  onClick={this.handleReset}
                >
                  Try Again
                </Button>,
                <Button
                  key="home"
                  onClick={this.handleGoHome}
                >
                  Back to Login
                </Button>
              ]}
            />
          </Layout.Content>
        </Layout>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;